/**
 * FinEase admin — Role Management shared UI classes.
 * Uses global tokens: brandDark, brandPrimary, primary, border, muted-foreground, card.
 */
export const RM = {
  page: "min-h-full bg-gray-50 p-6 font-sans text-foreground",
  container: "mx-auto max-w-7xl space-y-6",
  containerLoose: "mx-auto max-w-7xl space-y-8",

  h1: "text-2xl font-bold text-brandDark tracking-tight",
  h2: "text-lg font-bold text-brandDark",
  subtitle: "mt-1 text-sm text-muted-foreground",

  statBadge:
    "rounded-full border border-brandPrimary/40 bg-brandPrimary/10 px-4 py-1.5 text-sm font-medium text-brandDark",

  btnPrimary: "gap-2 bg-primary text-primary-foreground hover:bg-primary/90",

  card: "overflow-hidden rounded-2xl border border-border bg-card shadow-sm",
  cardGrid:
    "flex flex-col rounded-2xl border border-border bg-card p-6 shadow-sm transition-shadow hover:shadow-md",

  tableHead:
    "border-b border-border bg-brandPrimary/[0.08] text-xs font-semibold uppercase tracking-wide text-brandDark/80",
  tableRow:
    "border-b border-border/60 transition-colors hover:bg-brandPrimary/[0.04]",
  tableCell: "text-foreground",
  tableCellMuted: "text-muted-foreground",

  link: "font-medium text-brandPrimary underline underline-offset-2 transition-colors hover:text-brandDark",
  footnote: "text-center text-xs text-muted-foreground",

  modalTitle: "text-xl font-bold text-brandDark",
  label: "text-brandDark",
  input:
    "border-border bg-muted/50 text-foreground placeholder:text-muted-foreground focus-visible:border-brandPrimary/50 focus-visible:ring-brandPrimary/25",
  inputSelect: "border-border bg-muted/50",

  tabShell:
    "inline-flex rounded-full border border-border bg-card p-1 shadow-sm",
  tabActive:
    "rounded-full bg-primary px-6 py-2 text-sm font-semibold text-primary-foreground shadow",
  tabInactive:
    "rounded-full px-6 py-2 text-sm font-semibold text-muted-foreground transition-colors hover:text-brandDark",

  rolePill:
    "inline-flex rounded-full bg-brandDark px-3 py-1 text-xs font-semibold text-white",
  abbrBadge:
    "rounded-md bg-brandPrimary/15 px-2 py-0.5 text-xs font-bold text-brandDark",
  deptLabel: "mt-1 text-xs font-bold uppercase tracking-wide text-brandPrimary",

  iconCircle:
    "flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brandPrimary/10 text-primary",

  manageAccessIconWrap:
    "flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brandPrimary/10",

  permissionTableHeader:
    "flex items-center gap-2 border-b border-border bg-brandPrimary/[0.08] px-4 py-2 text-xs font-semibold uppercase tracking-wide text-brandDark/80",
  /** Outer frame for the permission matrix (border + radius) */
  permissionTableShell:
    "overflow-hidden rounded-xl border border-border bg-card shadow-sm",
  /** Matches checkbox column: fixed width + centered content */
  permissionColCheck: "flex w-24 shrink-0 items-center justify-center",
  /** Top-level module block inside permissionTableShell (no extra outer border) */
  permBox: "overflow-hidden border-b border-border/60 last:border-b-0",
  cloneSelectWrap: "rounded-lg border border-border bg-muted/40 p-1",

  outlineAccent: "border-brandPrimary/45 text-primary hover:bg-brandPrimary/10",

  emptyState:
    "rounded-xl border border-dashed border-border bg-card py-12 text-center text-muted-foreground",

  statusActive:
    "bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300",
  statusInactive: "bg-muted text-muted-foreground",

  /** Permission matrix rows (ManageAccessModal / CreateRoleModal) */
  permRow:
    "flex items-center gap-2 border-b border-border px-4 py-3 text-foreground",
  permRowNested: "border-t border-border bg-brandPrimary/[0.04] pl-6",
  permChevron: "text-muted-foreground hover:text-primary",
  permLabel: "truncate text-sm font-medium text-brandDark",
  permLabelBold: "truncate text-sm font-semibold text-brandDark",
  permNestedBlock: "border-l border-border",
} as const;
